// resources/js/Layouts/MainLayout.tsx
import React, {ReactNode, useState} from 'react';
import {
    AppBar,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Typography
} from '@mui/material';
import {Link, usePage} from '@inertiajs/react';
import {
    Add as AddIcon,
    AdminPanelSettings as AdminIcon,
    ArrowDropDown as ArrowDropDownIcon,
    Email as EmailIcon,
    EmojiEvents as EmojiEventsIcon,
    Gavel as GavelIcon,
    Help as HelpIcon,
    Instagram as InstagramIcon,
    OpenInBrowser as OpenInBrowserIcon,
    Person as PersonIcon,
    WhatsApp as WhatsappIcon
} from '@mui/icons-material';

interface MainLayoutProps {
    children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const props = usePage().props as any;
    const user = props.auth.user;

    // Menu state
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AppBar position="static" elevation={0}>
                <Toolbar variant={'dense'} sx={{minHeight: 56}}>
                    <Link href="/" style={{flexGrow: 1, padding: '8px'}}>
                        <img src={'/storage/logo-yo-compito.png'} width={140} alt="YoCompito" style={{ objectFit: 'contain' }} />
                    </Link>

                    {/* Navigation Links for Desktop */}
                    <Box sx={{display: {xs: 'none', md: 'flex'}, alignItems: 'center', gap: 1, mr: 2}}>
                        <Button color="inherit" component={Link} href="/" sx={{textTransform: 'none'}}>
                            <EmojiEventsIcon sx={{mr: 1, fontSize: '1.1rem'}}/>
                            Competitions
                        </Button>
                        {user && (
                            <Button
                                color="inherit"
                                component={Link}
                                href="/organizers/competitions/create"
                                sx={{textTransform: 'none'}}
                                variant="outlined"
                                size="small"
                            >
                                <AddIcon sx={{mr: 1, fontSize: '1rem'}}/>
                                Create
                            </Button>
                        )}
                    </Box>

                    {/* Instagram Browser Detection */}
                    {navigator.userAgent.includes("Instagram") && (
                        <Button
                            color="inherit"
                            startIcon={<OpenInBrowserIcon />}
                            component="a"
                            href={window.location.href}
                            target="_blank"
                            sx={{
                                mr: 2,
                                textTransform: 'none',
                                fontSize: '0.875rem'
                            }}
                        >
                            Abrir en navegador
                        </Button>
                    )}

                    {
                        user ? (
                            <>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2" sx={{display: {xs: 'none', sm: 'block'}, mr: 1}}>
                                        {user.name}
                                    </Typography>
                                    <IconButton
                                        onClick={handleClick}
                                        size="small"
                                        aria-controls={open ? 'account-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={open ? 'true' : undefined}
                                    >
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <Avatar
                                                sx={{width: 32, height: 32}}
                                                src={user.public_photo ? user.public_photo : undefined}
                                                alt={user.name}
                                            >
                                                {!user.public_photo && user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                            </Avatar>
                                            <ArrowDropDownIcon sx={{color: 'white'}}/>
                                        </Box>
                                    </IconButton>
                                </Stack>
                                <Menu
                                    anchorEl={anchorEl}
                                    id="account-menu"
                                    open={open}
                                    onClose={handleClose}
                                    onClick={handleClose}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                    PaperProps={{
                                        sx: {mt: 1, minWidth: 200}
                                    }}
                                >
                                    <MenuItem component={Link} href="/profile">
                                        <PersonIcon sx={{mr: 1}}/>
                                        Perfil de Atleta
                                    </MenuItem>
                                    <MenuItem component={Link} href="/organizers">
                                        <GavelIcon sx={{mr: 1}}/>
                                        Panel de Organizador
                                    </MenuItem>
                                    <MenuItem component={Link} href="/organizers/competitions/create"
                                              sx={{display: {md: 'none'}}}>
                                        <AddIcon sx={{mr: 1}}/>
                                        Crear Competencia
                                    </MenuItem>
                                    {user?.is_super_admin && (
                                        <>
                                            <Divider/>
                                            <MenuItem component={Link} href="/admin/kyc">
                                                <AdminIcon sx={{mr: 1}}/>
                                                KYC
                                            </MenuItem>
                                            <MenuItem component={Link} href="/admin/competition-fees">
                                                <AdminIcon sx={{mr: 1}}/>
                                                Tarifas de Competencias
                                            </MenuItem>
                                        </>
                                    )}
                                    <Divider />
                                    <MenuItem component={Link} href="/logout" sx={{color: 'error.main'}}>
                                        Salir
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : (
                            <Button
                                variant="outlined"
                                color="inherit"
                                component={Link}
                                href={`/login?redirect=${window.location.pathname}`}
                                sx={{textTransform: 'none'}}
                            >
                                Iniciar sesión
                            </Button>
                        )
                    }
                </Toolbar>
            </AppBar>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
                {children}
            </Container>

            {/* Enhanced Footer */}
            <Box
                component="footer"
                sx={{
                    py: 4,
                    px: 2,
                    mt: 'auto',
                    backgroundColor: (theme) => theme.palette.background.paper,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        {/* Brand Section */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: {xs: 'center', md: 'flex-start'}
                            }}>
                                <img src={'/storage/logo-yo-compito.png'} width={120} alt="YoCompito" style={{ marginBottom: '1rem' }} />
                                <Typography variant="body2" color="text.secondary"
                                            sx={{mb: 2, textAlign: {xs: 'center', md: 'left'}}}>
                                    The ultimate CrossFit competition platform connecting athletes and organizers
                                    worldwide.
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Chip label="Global" size="small" variant="outlined"/>
                                    <Chip label="Real-time" size="small" variant="outlined"/>
                                    <Chip label="Secure" size="small" variant="outlined"/>
                                </Stack>
                            </Box>
                        </Grid>

                        {/* Quick Links */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="h6" gutterBottom sx={{textAlign: {xs: 'center', md: 'left'}}}>
                                Quick Links
                            </Typography>
                            <Stack
                                spacing={1}
                                sx={{
                                    alignItems: {xs: 'center', md: 'flex-start'},
                                    '& a': {
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            color: 'primary.main',
                                            textDecoration: 'underline'
                                        }
                                    }
                                }}
                            >
                                <Link href="/">
                                    <EmojiEventsIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                    All Competitions
                                </Link>
                                {user ? (
                                    <>
                                        <Link href="/profile">
                                            <PersonIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                            My Profile
                                        </Link>
                                        <Link href="/organizers">
                                            <GavelIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                            Organizer Panel
                                        </Link>
                                        <Link href="/organizers/competitions/create">
                                            <AddIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                            Create Competition
                                        </Link>
                                    </>
                                ) : (
                                    <Link href="/login">
                                        <PersonIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                        Login / Register
                                    </Link>
                                )}
                            </Stack>
                        </Grid>

                        {/* Support */}
                        <Grid item xs={12} sm={6} md={2}>
                            <Typography variant="h6" gutterBottom sx={{textAlign: {xs: 'center', md: 'left'}}}>
                                Support
                            </Typography>
                            <Stack
                                spacing={1}
                                sx={{
                                    alignItems: {xs: 'center', md: 'flex-start'},
                                    '& a': {
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            color: 'primary.main',
                                            textDecoration: 'underline'
                                        }
                                    }
                                }}
                            >
                                <a href="https://wa.me/543816525385" target="_blank" rel="noopener noreferrer">
                                    <HelpIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                    Help Center
                                </a>
                                <a href="https://wa.me/543816525385" target="_blank" rel="noopener noreferrer">
                                    <WhatsappIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                    WhatsApp Support
                                </a>
                                <a href="mailto:contact@yocompito.com">
                                    <EmailIcon sx={{mr: 1, fontSize: '1rem', verticalAlign: 'middle'}}/>
                                    Contact Us
                                </a>
                            </Stack>
                        </Grid>

                        {/* Social & Connect */}
                        <Grid item xs={12} md={3}>
                            <Typography variant="h6" gutterBottom sx={{textAlign: {xs: 'center', md: 'left'}}}>
                                Connect With Us
                            </Typography>
                            <Typography variant="body2" color="text.secondary"
                                        sx={{mb: 2, textAlign: {xs: 'center', md: 'left'}}}>
                                Follow us for updates, tips, and community highlights.
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{justifyContent: {xs: 'center', md: 'flex-start'}}}
                            >
                                <IconButton
                                    aria-label="Instagram"
                                    color="primary"
                                    component="a"
                                    href="https://instagram.com/yocompito"
                                    target="_blank"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <InstagramIcon />
                                </IconButton>
                                <IconButton
                                    aria-label="WhatsApp"
                                    color="primary"
                                    component="a"
                                    href="https://wa.me/543816525385"
                                    target="_blank"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <WhatsappIcon />
                                </IconButton>
                                <IconButton
                                    aria-label="Email"
                                    color="primary"
                                    component="a"
                                    href="mailto:contact@yocompito.com"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <EmailIcon/>
                                </IconButton>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Box mt={4}>
                        <Divider />
                        <Stack
                            direction={{xs: 'column', sm: 'row'}}
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={2}
                            sx={{pt: 3}}
                        >
                            <Typography variant="body2" color="text.secondary">
                                © {new Date().getFullYear()} YoCompito. All rights reserved.
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {color: 'primary.main'}
                                    }}
                                >
                                    Privacy Policy
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {color: 'primary.main'}
                                    }}
                                >
                                    Terms of Service
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default MainLayout;
